import React, { useEffect, useState } from "react";
import { urlDoAvatar } from "services/avatarService";

const COLORS = ["#D9466E", "#BE345A", "#FB7185", "#b45309", "#9f1239", "#4338ca", "#065f46"];

const getInitials = (name) => {
    if (!name) return "--";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getColor = (name) => {
    if (!name) return COLORS[0];
    const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return COLORS[hash % COLORS.length];
};

/**
 * O avatar de uma pessoa: a foto de perfil quando existe, as iniciais sobre uma cor derivada do nome
 * quando não.
 *
 * `avatar` é o nome do arquivo que a API devolve (não a URL): quem monta a URL é
 * services/avatarService, porque ela depende da origem da API e o app nativo precisa dela absoluta.
 *
 * As iniciais são o fallback em dois momentos, não só um: sem `avatar`, e quando o `<img>` falha —
 * arquivo apagado, rede fora, cache do proxy sujo. Sem o segundo, a pessoa apareceria como o ícone
 * de imagem quebrada do navegador, que é pior do que nunca ter tido foto. A falha é esquecida quando
 * o nome muda (troca de foto): a URL nova merece uma tentativa nova.
 */
const InitialsAvatar = ({ name, avatar, className = "h-10 w-10 rounded-xl text-sm" }) => {
    const [falhou, setFalhou] = useState(false);

    useEffect(() => {
        setFalhou(false);
    }, [avatar]);

    const src = !falhou ? urlDoAvatar(avatar) : null;

    if (src) {
        return (
            <img
                src={src}
                alt={`Foto de ${name}`}
                title={name}
                loading="lazy"
                decoding="async"
                onError={() => setFalhou(true)}
                className={`shrink-0 object-cover ${className}`}
            />
        );
    }

    return (
        <div
            className={`flex shrink-0 items-center justify-center font-semibold text-white ${className}`}
            style={{ backgroundColor: getColor(name) }}
            aria-label={`Avatar de ${name}`}
            title={name}
        >
            {getInitials(name)}
        </div>
    );
};

export default InitialsAvatar;
